import React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { List, ListItem, ListItemText } from '@mui/material';
import { Assignment, Business, ChevronRight, EmojiEvents, Groups, MenuBook, Payment, ReceiptLong, Spa, Star, SupportAgent } from '@mui/icons-material';
import { AccountBoxMultiple, ClipboardList, NewspaperVariantOutline, TableSearch } from 'mdi-material-ui';
import StyledMoreProfileSettingsIcon from 'common/theme/more-profile/StyledMoreProfileSettingsIcon';
import routes from 'common/routes';

function ClientMoreHub() {
  const { t } = useTranslation(['routes', 'pqrs']);
  const platform = useSelector((state) => state.auth.user.platform);
  const membershipStatus = useSelector((state) => state.auth.user.membership_status);
  const isActivePlus = useSelector((state) => state.auth.user.is_active_plus);
  const hasRegularPlan = useSelector((state) => Boolean(state.auth.user?.plan_id?.id));
  const hasOneOnOne = useSelector((state) => state.auth.user.has_one_on_one_sessions);
  const isGym = platform === 'otrix_gym';
  const isOtrixPlay = platform === 'otrix_play';
  const isInactive = membershipStatus === 'INACTIVE';

  const allItems = [
    {
      // "Mis sesiones" = sesiones PERSONALIZADAS (individuales con plan).
      icon: <TableSearch />,
      label: t('session.mySessions', { ns: 'routes' }),
      to: routes.clientSessions.path,
      tour: 'my-sessions',
      gymHidden: false,
      activePlusHidden: false
    },
    {
      icon: <NewspaperVariantOutline />,
      label: t('news.list', { ns: 'routes' }),
      to: routes.listNews.path,
      tour: 'news',
      gymHidden: false,
      activePlusHidden: false
    },
    {
      icon: <EmojiEvents />,
      label: t('gamification.globalRanking', { ns: 'routes' }),
      to: routes.globalRanking.path,
      tour: 'ranking',
      gymHidden: true,
      activePlusHidden: false,
      // Ranking Global desaparece en OTRIX Play (queda solo Mi rango + Rendimientos).
      playHidden: true
    },
    {
      icon: <Star />,
      label: t('gamification.myRank', { ns: 'routes' }),
      to: isOtrixPlay ? routes.playMyRank.path : routes.myGamificationProfile.path,
      tour: 'gamification',
      gymHidden: true,
      activePlusHidden: false
    },
    {
      icon: <Payment />,
      label: t('payment.payments', { ns: 'routes' }),
      to: routes.listClientPayments.client.path,
      tour: 'payments',
      gymHidden: false,
      activePlusHidden: false
    },
    {
      icon: <ReceiptLong />,
      label: t('payment.chargesHistory', { ns: 'routes', defaultValue: 'Historial de cobros' }),
      to: routes.clientChargesHistory.path,
      tour: 'charges',
      gymHidden: false,
      activePlusHidden: false
    },
    {
      icon: <AccountBoxMultiple />,
      label: t('progress.list', { ns: 'routes' }),
      to: routes.clientProgress.path,
      tour: 'progress',
      gymHidden: true,
      // Visible también en Active Plus: es el canal donde el entrenador acompaña el avance, así
      // que esconderle las fotos de progreso era justo al revés de lo que tiene sentido.
      activePlusHidden: false
    },
    {
      icon: <MenuBook />,
      label: t('clientProgram', { ns: 'routes', defaultValue: 'Mi Programa' }),
      to: routes.clientProgram.path,
      tour: 'my-program',
      gymHidden: false,
      activePlusHidden: false
    },
    {
      icon: <Business />,
      label: t('myOrganization', { ns: 'routes', defaultValue: 'Mi organización' }),
      to: routes.joinOrganization.path,
      tour: 'my-organization',
      gymHidden: false,
      activePlusHidden: false
    },
    {
      icon: <Spa />,
      label: t('myWellbeing', { ns: 'routes', defaultValue: 'Mi bienestar' }),
      to: routes.myWellbeing.path,
      tour: 'my-wellbeing',
      gymHidden: false,
      activePlusHidden: false
    },
    {
      icon: <Assignment />,
      label: t('legal.list', { ns: 'routes' }),
      to: routes.viewTC.path,
      tour: 'legal',
      gymHidden: false,
      activePlusHidden: false
    },
    {
      icon: <SupportAgent />,
      label: t('menu', { ns: 'pqrs', defaultValue: 'Soporte (PQRS)' }),
      to: routes.myPqrs.path,
      tour: 'pqrs',
      gymHidden: false,
      activePlusHidden: false
    }
  ];

  const planItem = isInactive
    ? [{ icon: <ClipboardList />, label: t('plan.myPlan', { ns: 'routes' }), to: routes.myPlanStatus.path, gymHidden: false, activePlusHidden: false }]
    : [];

  // "Sesiones grupales" (Active Plus) — solo para clientes active plus.
  const groupItem = isActivePlus
    ? [{ icon: <Groups />, label: t('session.groupSessions', { ns: 'routes' }), to: routes.activePlusSessions.path, gymHidden: false, activePlusHidden: false }]
    : [];

  // Unión: un cliente puede tener plan regular Y Active Plus. Se muestra un ítem si es relevante
  // para CUALQUIERA de sus capacidades (canal regular y/o Active Plus).
  const channels = [];
  if (isActivePlus) channels.push('active_plus');
  if (hasRegularPlan) channels.push(isGym ? 'gym' : 'app');
  if (channels.length === 0) channels.push('app');

  const isItemVisible = (item) => channels.some((channel) => {
    if (channel === 'gym') return !item.gymHidden;
    if (channel === 'active_plus') return !item.activePlusHidden;
    return true; // 'app' (otrix_app) muestra todo
  });

  const listItems = [
    ...planItem,
    ...groupItem,
    ...allItems.filter(isItemVisible)
  ]
    // OTRIX Play no tiene sesiones 1:1 salvo un plan adicional (pilates): ocultar "Mis sesiones".
    .filter((item) => !(isOtrixPlay && !hasOneOnOne && item.to === routes.clientSessions.path))
    // Ítems ocultos en OTRIX Play (p. ej. Ranking Global).
    .filter((item) => !(isOtrixPlay && item.playHidden));

  return (
    <List>
      {listItems.map((item) => (
        <ListItem
          button
          component={Link}
          divider
          key={item.label}
          secondaryAction={<ChevronRight />}
          to={item.to}
          // Prefijo "more-" para NO colisionar con las anclas del sidebar de escritorio (que sigue
          // en el DOM, oculto por CSS) → el tour móvil resalta el ítem visible de "Más".
          data-tour={item.tour ? `more-${item.tour}` : undefined}
          sx={{ pl: 0 }}>
          <StyledMoreProfileSettingsIcon>{item.icon}</StyledMoreProfileSettingsIcon>
          <ListItemText primary={item.label} />
        </ListItem>
      ))}
    </List>
  );
}

export default ClientMoreHub;
